import { useMutation } from "@apollo/client/react";
import {
  ReorderStudyProgrammeElementInput,
  ReorderStudyProgrammeElementVariables,
  StudyProgrammeAdminPageData,
  StudyProgrammeAdminPageDataVariables,
  StudyProgrammeAdminPageData_studyProgrammeById,
  StudyProgrammeAdminPageData_studyProgrammeById_elements,
} from "__generated__/types";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NormalTabbedLayout } from "common/components/layouts/NormalTabbedLayout";
import { NoDataError } from "common/components/NoDataError";
import { Select } from "common/components/Select";
import { SubText } from "common/components/Typography";
import { contentStatusFilterValueToSelectOption } from "common/content-status-filter";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { urls } from "common/urls";
import {
  ListElement,
  ListElementStudyProgrammeElement,
} from "domain/admin/common/AdministrationList";
import { CreateContent } from "domain/admin/common/CreateContent";
import { CreateContentFooterBox } from "domain/admin/common/CreateContentFooterBox";
import { adminStudyProgrammeFragment } from "domain/admin/fragments";
import { isInSyllabus } from "domain/common/utils";
import { ContentStatusFilterValue } from "domain/constants";
import { contentBreadcrumbFragment } from "domain/pages/breadcrumbs";
import gql from "graphql-tag";
import React from "react";
import { Link, useParams } from "react-router-dom";
import { getListElementType } from "../common/utils";
import { chainFilters, noDeletedStatusFilter, statusFilter } from "../util";

// Dynamic import to cause code split for react-dnd library.
const AdministrationList = React.lazy(() =>
  import("domain/admin/common/AdministrationList").then((it) => ({
    default: it.AdministrationList,
  })),
);

const GET_STUDY_PROGRAMME = gql`
  query StudyProgrammeAdminPageData($studyProgrammeId: ID!) {
    studyProgrammeById(id: $studyProgrammeId) {
      ...ContentBreadcrumbFragment
      ...AdminStudyProgrammeFragment
    }
  }
  ${contentBreadcrumbFragment}
  ${adminStudyProgrammeFragment}
`;

type RouteProps = {
  studyProgrammeId: string;
};

const MUTATION = gql`
  mutation ReorderStudyProgrammeElement(
    $input: ReorderStudyProgrammeElementInput!
  ) {
    reorderStudyProgrammeElement(input: $input) {
      studyProgramme {
        id
      }
    }
  }
`;

const convertToList = (
  data: StudyProgrammeAdminPageData_studyProgrammeById_elements[],
  studyProgramme: StudyProgrammeAdminPageData_studyProgrammeById,
): ListElement[] => {
  return data.map((it) => ({
    category: "StudyProgrammeElement",
    id: it.id,
    type: getListElementType(it.content),
    status: it.content.status,
    studyProgrammeElementId: it.content.id,
    studyProgrammeId: studyProgramme.id,
    title: it.content.title,
    contentId: it.content.id,
  }));
};

export const StudyProgrammeAdminPage: React.FC = () => {
  const { studyProgrammeId } = useParams<RouteProps>();

  const [contentStatusFilterValue, setContentStatusFilterValue] =
    React.useState<ContentStatusFilterValue>(ContentStatusFilterValue.ALL);

  if (!studyProgrammeId) {
    throw new Error("Unexpected error: Missing studyProgrammeId");
  }

  const { data, loadingOrError, refetch } = useSimpleQuery<
    StudyProgrammeAdminPageData,
    StudyProgrammeAdminPageDataVariables
  >(GET_STUDY_PROGRAMME, {
    variables: {
      studyProgrammeId,
    },
  });

  const [mutate, { error }] = useMutation<
    ReorderStudyProgrammeElementInput,
    ReorderStudyProgrammeElementVariables
  >(MUTATION);

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.studyProgrammeById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const handleSaveListOrder = async (
    id: string,
    moveToAfterElement: ListElementStudyProgrammeElement | null,
  ) => {
    await mutate({
      variables: {
        input: {
          studyProgrammeId,
          elementId: id,
          moveToAfterElementId: moveToAfterElement?.id,
        },
      },
    });

    return refetch();
  };

  const filteredElementsList = data.studyProgrammeById.elements.filter(
    chainFilters(
      (e) => noDeletedStatusFilter(e.content.status),
      statusFilter(contentStatusFilterValue),
    ),
  );

  const TabContent: React.FC<{ type: string }> = ({ type }) => {
    return (
      <>
        <Select
          title="Filtrer på status"
          name="status"
          options={Object.values(ContentStatusFilterValue).map(
            contentStatusFilterValueToSelectOption,
          )}
          value={contentStatusFilterValue.toString()}
          onChange={(newValue: ContentStatusFilterValue) => {
            setContentStatusFilterValue(newValue);
          }}
        />
        {data.studyProgrammeById.elements.length === 0 ? (
          <p>Studieprogrammet har ikke noe innhold enda.</p>
        ) : filteredElementsList.length === 0 ? (
          <p>Studieprogrammet har ikke noe innhold som passer filteret ditt.</p>
        ) : (
          <AdministrationList
            showElementIcons
            isInSyllabus={isInSyllabus(data.studyProgrammeById.context)}
            elements={convertToList(
              filteredElementsList.filter((e) => e.content.__typename === type),
              data.studyProgrammeById,
            )}
            canDelete
            complete={refetch}
            onSaveListOrder={handleSaveListOrder}
          />
        )}
      </>
    );
  };

  return (
    <NormalTabbedLayout
      title={data.studyProgrammeById.title}
      context={data.studyProgrammeById.context ?? []}
      isAdmin={true}
      aboveTabs={
        <SubText>
          <Link to={urls.studyProgrammeAdminEdit(data.studyProgrammeById.id)}>
            Rediger detaljer
          </Link>
          <br />
          <Link to={urls.studyProgramme(data.studyProgrammeById.id)}>
            Studentvisning
          </Link>
        </SubText>
      }
      navigationTabs={[
        {
          title: "Undervisningsmateriell",
          path: urls.studyProgrammeAdmin(studyProgrammeId),
          content: <TabContent type="Folder" />,
        },
        {
          title: "Pensum",
          path: urls.studyProgrammeAdminExams(studyProgrammeId),
          content: <TabContent type="ExamSyllabus" />,
        },
      ]}
      footer={
        <>
          <MutationLoadingOrError
            // TODO: Do we want to show loading indication when changing order?
            loading={false}
            error={error}
          />
          <CreateContentFooterBox
            buttonText="Legg til innhold"
            content={(props) => (
              <CreateContent
                {...props}
                parent="studyProgramme"
                studyProgramme={data.studyProgrammeById}
                parentId={studyProgrammeId}
              />
            )}
            onUpdated={refetch}
          />
        </>
      }
    />
  );
};
